'use client';
import React, { useState, useEffect, useCallback } from 'react';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/lib/supabase/client';

interface CourierVessel {
  id: string;
  vessel_name: string;
  vessel_type: string;
  registration_number: string;
  max_capacity_kg: number;
  description: string;
  user_profiles?: { full_name: string; trust_score: number; total_deliveries: number };
  courier_vessel_photos?: { photo_url: string; is_primary: boolean }[];
}

export default function BrowseCouriersContent() {
  const supabase = createClient();
  const [vessels, setVessels] = useState<CourierVessel[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [selected, setSelected] = useState<CourierVessel | null>(null);

  const fetchVessels = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await supabase
        .from('courier_vessels')
        .select(`
          *,
          user_profiles!courier_vessels_user_id_fkey(full_name, trust_score, total_deliveries, courier_approval_status),
          courier_vessel_photos(photo_url, is_primary)
        `)
        .eq('is_active', true)
        .order('created_at', { ascending: false });
      // Only show approved couriers
      const approved = (data || []).filter((v: any) => v.user_profiles?.courier_approval_status === 'approved');
      setVessels(approved);
    } catch (err) {
      console.error('Error fetching couriers:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchVessels(); }, [fetchVessels]);

  const vesselTypes = ['all', ...Array.from(new Set(vessels.map(v => v.vessel_type).filter(Boolean)))];

  const filtered = vessels.filter(v => {
    if (typeFilter !== 'all' && v.vessel_type !== typeFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      const profile = v.user_profiles as any;
      if (!(`${v.vessel_name} ${v.vessel_type} ${profile?.full_name || ''}`).toLowerCase().includes(q)) return false;
    }
    return true;
  });

  return (
    <div className="space-y-6 p-4 sm:p-6">
      {/* Header */}
      <div className="space-y-1">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center">
            <Icon name="TruckIcon" size={16} className="text-white" />
          </div>
          <h1 className="text-2xl font-bold text-foreground">Browse Couriers</h1>
        </div>
        <p className="text-sm text-muted-foreground">Verified professional couriers — up to 5 tonnes capacity</p>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Icon name="MagnifyingGlassIcon" size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search couriers..." className="w-full pl-8 pr-3 py-2 rounded-lg border border-input bg-card text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring" />
        </div>
        <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)} className="px-3 py-2 rounded-lg border border-input bg-card text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring">
          {vesselTypes.map(t => <option key={t} value={t}>{t === 'all' ? 'All Types' : t}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Icon name="ArrowPathIcon" size={28} className="animate-spin text-muted-foreground" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16">
          <Icon name="TruckIcon" size={36} className="text-muted-foreground mx-auto mb-3" />
          <p className="text-muted-foreground">No approved couriers found</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {filtered.map(vessel => {
            const profile = vessel.user_profiles as any;
            const photos = vessel.courier_vessel_photos || [];
            const primaryPhoto = photos.find(p => p.is_primary)?.photo_url || photos[0]?.photo_url;
            return (
              <div key={vessel.id} className="card-elevated overflow-hidden cursor-pointer hover:shadow-lg transition-shadow" onClick={() => setSelected(vessel)}>
                {/* Photo */}
                <div className="h-40 bg-slate-100 dark:bg-slate-800 relative overflow-hidden">
                  {primaryPhoto ? (
                    <img src={primaryPhoto} alt={vessel.vessel_name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Icon name="TruckIcon" size={40} className="text-slate-300" />
                    </div>
                  )}
                  <div className="absolute top-2 right-2 bg-blue-600 text-white text-xs font-semibold px-2 py-1 rounded-full">
                    {vessel.vessel_type}
                  </div>
                  {photos.length > 1 && (
                    <div className="absolute bottom-2 right-2 bg-black/60 text-white text-xs px-2 py-0.5 rounded-full">
                      +{photos.length - 1} photos
                    </div>
                  )}
                </div>
                <div className="p-4 space-y-2">
                  <div>
                    <p className="font-semibold text-foreground">{vessel.vessel_name}</p>
                    <p className="text-xs text-muted-foreground">{profile?.full_name || 'Courier'}</p>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <Icon name="ScaleIcon" size={12} />
                      Max {vessel.max_capacity_kg?.toLocaleString()} kg
                    </span>
                    {vessel.registration_number && (
                      <span className="flex items-center gap-1">
                        <Icon name="IdentificationIcon" size={12} />
                        {vessel.registration_number}
                      </span>
                    )}
                  </div>
                  {profile?.trust_score > 0 && (
                    <div className="flex items-center gap-1 text-xs">
                      <span className="text-yellow-500">★</span>
                      <span className="font-medium text-foreground">{profile.trust_score?.toFixed(1)}</span>
                      <span className="text-muted-foreground">· {profile.total_deliveries || 0} deliveries</span>
                    </div>
                  )}
                  {vessel.description && (
                    <p className="text-xs text-muted-foreground line-clamp-2">{vessel.description}</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Detail modal */}
      {selected && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setSelected(null)}>
          <div className="bg-card rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-5 border-b border-border">
              <h3 className="font-bold text-foreground">{selected.vessel_name}</h3>
              <button onClick={() => setSelected(null)} className="p-1.5 rounded-lg hover:bg-muted transition-colors">
                <Icon name="XMarkIcon" size={18} className="text-muted-foreground" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              {/* Photos gallery */}
              {(selected.courier_vessel_photos || []).length > 0 && (
                <div className="flex gap-2 overflow-x-auto pb-1">
                  {selected.courier_vessel_photos!.map((p, i) => (
                    <img key={i} src={p.photo_url} alt={`${selected.vessel_name} photo ${i + 1}`} className="w-32 h-24 rounded-lg object-cover flex-shrink-0 border border-border" />
                  ))}
                </div>
              )}
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Courier</span>
                  <span className="text-sm font-medium text-foreground">{(selected.user_profiles as any)?.full_name}</span>
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="bg-muted rounded-lg p-3">
                    <p className="text-xs text-muted-foreground">Type</p>
                    <p className="font-semibold text-foreground">{selected.vessel_type}</p>
                  </div>
                  <div className="bg-muted rounded-lg p-3">
                    <p className="text-xs text-muted-foreground">Max Capacity</p>
                    <p className="font-semibold text-foreground">{selected.max_capacity_kg?.toLocaleString()} kg</p>
                  </div>
                  {selected.registration_number && (
                    <div className="bg-muted rounded-lg p-3">
                      <p className="text-xs text-muted-foreground">Registration</p>
                      <p className="font-semibold text-foreground">{selected.registration_number}</p>
                    </div>
                  )}
                  {(selected.user_profiles as any)?.trust_score > 0 && (
                    <div className="bg-muted rounded-lg p-3">
                      <p className="text-xs text-muted-foreground">Rating</p>
                      <p className="font-semibold text-foreground">★ {(selected.user_profiles as any).trust_score?.toFixed(1)} ({(selected.user_profiles as any).total_deliveries || 0} deliveries)</p>
                    </div>
                  )}
                </div>
                {selected.description && (
                  <div>
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">About</p>
                    <p className="text-sm text-foreground">{selected.description}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
